import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { SubscriptionStatus, HardwareSubscription } from "@providers/profile/ProfileProvider.model";
import SubscriptionUnknown from "./SubscriptionUnknown";
import SubscriptionInactive from "./SubscriptionInactive";
import SubscriptionDetail from "./SubscriptionDetail";
import SubscriptionDigital from "./SubscriptionDigital";
import { SubscriptionInfoProps, dateFormat } from "./Subscription.model";
import { Alert, NonActiveSubscription } from "../Account.styled";

const willChargeAgainNextMonth = (final_billing_date: string) => {
  return !!final_billing_date && Date.parse(final_billing_date) > Date.now();
};

const showFuturePaymentMessage = (subscription: HardwareSubscription) => {
  return (
    !subscription.auto_renew &&
    subscription.subscription_status !== SubscriptionStatus.Canceled &&
    willChargeAgainNextMonth(subscription.final_billing_date)
  );
};

const futurePaymentMessage = (subscription: HardwareSubscription) => {
  if (showFuturePaymentMessage(subscription)) {
    return (
      <Alert>
        Auto-Renew has been turned off. You have {subscription.remaining_commitment_months}{" "}
        remaining payments left, and your final charge will be on{" "}
        {format(parseISO(subscription.final_billing_date), dateFormat)}
      </Alert>
    );
  }
};

const SubscriptionNone = () => (
  <article>
    <NonActiveSubscription>No available subscription</NonActiveSubscription>
  </article>
);

const SubscriptionCanceled = () => (
  <article>
    <NonActiveSubscription>
      You had a Mirror Digital Trial which has ended, please{" "}
      <a href="https://www.mirror.co/shop/mirror">purchase a Mirror</a> to continue your
      subscription
    </NonActiveSubscription>
  </article>
);

const SubscriptionTrial = ({ activation_date }: { activation_date: string }) => (
  <article>
    <NonActiveSubscription>
      You are currently on a Mirror Digital Trial, which expires{" "}
      {format(parseISO(activation_date), dateFormat)}
    </NonActiveSubscription>
  </article>
);

const SubscriptionPending = () => (
  <NonActiveSubscription>
    <p>Your Subscription will activate once your Mirror has been delivered.</p>
  </NonActiveSubscription>
);

const SubscriptionInfo = ({
  isDigital,
  digitalDisclaimers,
  subscription,
  dispatchBilling,
}: SubscriptionInfoProps) => {
  if (isDigital) {
    return <SubscriptionDigital digitalDisclaimers={digitalDisclaimers} />;
  }

  if (!subscription || subscription?.error || !subscription?.subscription_status) {
    return <SubscriptionNone />;
  }

  if (
    process.env.NEXT_PUBLIC_STRIPE_TRIAL_PLAN_ID === subscription.subscription_id &&
    subscription.subscription_status === SubscriptionStatus.Canceled
  ) {
    return <SubscriptionCanceled />;
  }

  if (
    process.env.NEXT_PUBLIC_STRIPE_TRIAL_PLAN_ID === subscription.subscription_id &&
    subscription.subscription_activation_date
  ) {
    return <SubscriptionTrial activation_date={subscription.subscription_activation_date} />;
  }

  const SubscriptionComponent = () => {
    switch (subscription.subscription_status) {
      case SubscriptionStatus.Unknown:
        return <SubscriptionUnknown />;
      case SubscriptionStatus.Trialing:
        return <SubscriptionPending />;
      case SubscriptionStatus.Canceled:
      case SubscriptionStatus.PastDue:
      case SubscriptionStatus.Unpaid:
        return (
          <SubscriptionInactive subscription={subscription} dispatchBilling={dispatchBilling} />
        );
      case SubscriptionStatus.Active:
        return <SubscriptionDetail subscription={subscription} />;
      default:
        break;
    }
  };

  return (
    <article>
      {futurePaymentMessage(subscription)}
      <SubscriptionComponent />
    </article>
  );
};

export default SubscriptionInfo;
